<script setup>
/**
 * @description 手风琴复选框组件，支持多级嵌套递归结构，参考 a-tree 交互
 * @param {Array} options - 选项数据 [{ label, value, children: [] }]
 * @param {boolean} cascade - 是否级联选择（父项勾选时自动勾选子项）
 * @param {number} level - 当前层级（用于样式控制），默认为 1
 * @emits update:modelValue - 选中项改变时触发
 */

// 1. Imports
import { IconDown } from '@arco-design/web-vue/es/icon'
import { ref } from 'vue'

// 2. Props & Models
const props = defineProps({
  options: {
    type: Array,
    default: () => [],
  },
  cascade: {
    type: Boolean,
    default: true,
  },
  level: {
    type: Number,
    default: 1,
  },
})

const modelValue = defineModel({
  type: Array,
  default: () => [],
})

const emit = defineEmits(['check-change'])

// 4. Refs/Reactive State
const expandedKeys = ref([])

// 5. Methods
/**
 * @description 获取所有后代的值（包括自身）
 * @param {Object} option
 */
const getAllDescendantValues = (option) => {
  let values = [option.value]
  if (option.children && option.children.length > 0) {
    option.children.forEach((child) => {
      values = values.concat(getAllDescendantValues(child))
    })
  }
  return values
}

/**
 * @description 切换展开状态
 * @param {string} value - 选项的值
 */
const toggleExpand = (value) => {
  const index = expandedKeys.value.indexOf(value)
  if (index > -1) {
    expandedKeys.value.splice(index, 1)
  } else {
    expandedKeys.value.push(value)
  }
}

/**
 * @description 检查是否展开
 * @param {string} value
 */
const isExpanded = (value) => expandedKeys.value.includes(value)

/**
 * @description 处理复选框点击
 * @param {boolean} checked - 是否选中
 * @param {Object} option - 当前选项
 */
const handleCheck = (checked, option) => {
  let newValue = [...modelValue.value]

  if (!props.cascade) {
    if (checked) {
      if (!newValue.includes(option.value)) newValue.push(option.value)
    } else {
      newValue = newValue.filter((v) => v !== option.value)
    }
    modelValue.value = newValue
    emit('check-change', newValue)
    return
  }

  const valuesToUpdate = getAllDescendantValues(option)

  if (checked) {
    valuesToUpdate.forEach((v) => {
      if (!newValue.includes(v)) newValue.push(v)
    })
  } else {
    newValue = newValue.filter((v) => !valuesToUpdate.includes(v))
  }

  modelValue.value = newValue
  emit('check-change', newValue)
}

/**
 * @description 处理子级复选框变更
 * @param {Object} parentOption
 * @param {Array} currentValues - 子级传上来的最新值
 */
const onChildCheckChange = (parentOption, currentValues) => {
  if (!props.cascade) {
    emit('check-change', currentValues)
    return
  }

  const newValue = [...currentValues]
  const allChildrenChecked = parentOption.children.every((child) => newValue.includes(child.value))

  const parentValue = parentOption.value
  const hasParent = newValue.includes(parentValue)

  if (allChildrenChecked && !hasParent) {
    newValue.push(parentValue)
  } else if (!allChildrenChecked && hasParent) {
    const index = newValue.indexOf(parentValue)
    if (index > -1) newValue.splice(index, 1)
  }

  // 即使没变也继续往上传，确保整个链路同步
  modelValue.value = newValue
  emit('check-change', newValue)
}

/**
 * @description 获取选中状态
 * @param {Object} option
 */
const getCheckStatus = (option) => {
  return modelValue.value.includes(option.value)
}

/**
 * @description 获取半选状态
 * @param {Object} option
 */
const isIndeterminate = (option) => {
  if (!option.children || option.children.length === 0) return false

  // 如果自身已选中，则不显示半选
  if (getCheckStatus(option)) return false

  // 只要后代有被选中的，就是半选
  const descendantValues = getAllDescendantValues(option).filter((v) => v !== option.value)
  return descendantValues.some((v) => modelValue.value.includes(v))
}

// 6. Transition Hooks
const onEnter = (el) => {
  el.style.height = '0'
  el.offsetHeight // force reflow
  el.style.height = el.scrollHeight + 'px'
}

const onAfterEnter = (el) => {
  el.style.height = ''
}

const onLeave = (el) => {
  el.style.height = el.scrollHeight + 'px'
  el.offsetHeight // force reflow
  el.style.height = '0'
}

const onAfterLeave = (el) => {
  el.style.height = ''
}
</script>

<template>
  <div class="filter-accordion">
    <div v-for="item in options" :key="item.value" class="filter-accordion__group">
      <!-- 选项主体 -->
      <div class="filter-accordion__item" @click="toggleExpand(item.value)">
        <a-checkbox
          :model-value="getCheckStatus(item)"
          :indeterminate="isIndeterminate(item)"
          @change="(checked) => handleCheck(checked, item)"
          @click.stop
        >
          <span class="filter-accordion__label" :title="item.label">
            {{ item.label }}
          </span>
        </a-checkbox>
        <div class="filter-accordion__spacer"></div>
        <template v-if="item.children && item.children.length > 0">
          <IconDown
            class="filter-accordion__icon"
            :class="{ 'filter-accordion__icon--expanded': isExpanded(item.value) }"
          />
        </template>
      </div>

      <!-- 子级选项 (递归调用) -->
      <Transition
        name="accordion"
        @enter="onEnter"
        @after-enter="onAfterEnter"
        @leave="onLeave"
        @after-leave="onAfterLeave"
      >
        <div
          v-if="item.children && item.children.length > 0 && isExpanded(item.value)"
          class="filter-accordion__sub-options"
        >
          <BaseFilterAccordion
            v-model="modelValue"
            :options="item.children"
            :cascade="cascade"
            :level="props.level + 1"
            @check-change="(val) => onChildCheckChange(item, val)"
          />
        </div>
      </Transition>
    </div>
  </div>
</template>

<style scoped lang="scss">
.filter-accordion {
  display: flex;
  flex-direction: column;
  gap: 12px;

  &__group {
    display: flex;
    flex-direction: column;
  }

  &__item {
    display: flex;
    align-items: flex-start;
    justify-content: flex-start;
    cursor: pointer;
    min-height: 22px;
    min-width: 0;
  }

  &__spacer {
    flex: 1;
    min-width: 0;
    height: 100%;
  }

  &__label {
    display: block;
    max-width: 100%;
    white-space: normal;
    overflow-wrap: break-word;
    word-break: break-word;
    line-height: $line-height-md;
  }

  &__icon {
    flex-shrink: 0;
    font-size: 14px;
    color: $text-subtitle-color;
    transition: all 0.3s ease;

    &--expanded {
      transform: rotate(180deg);
    }
  }

  &__sub-options {
    margin-top: 12px;
    padding-left: 24px;
    display: flex;
    flex-direction: column;
    gap: 12px;
    line-height: $line-height-md;
  }

  :deep(.arco-checkbox) {
    display: inline-flex;
    align-items: flex-start;
    flex: 1 1 auto;
    min-width: 0;
    margin-right: 8px;

    .arco-checkbox-label {
      overflow: visible;
      text-overflow: clip;
      white-space: normal;
      flex: 1;
      min-width: 0;
      line-height: $line-height-md;
      overflow-wrap: break-word;
      word-break: break-word;
    }

    .arco-checkbox-icon {
      flex-shrink: 0;
      margin-top: 2px;
    }
  }
}

.accordion-enter-active,
.accordion-leave-active {
  transition: all 0.3s cubic-bezier(0.645, 0.045, 0.355, 1);
  overflow: hidden;
}

.accordion-enter-from,
.accordion-leave-to {
  opacity: 0;
  height: 0 !important;
  margin-top: 0 !important;
  padding-top: 0 !important;
  padding-bottom: 0 !important;
}
</style>
